/**
 * VertraegePage (Phase 6, §6.2). Lists contracts (recurring payments) with an
 * Auffälligkeiten strip, the active list (row click → edit dialog, „Verlauf"
 * link → filtered transactions), a suggestions section from booked-history
 * detection, inactive contracts collapsed at the bottom, and a client-side
 * monthly Fixkosten sum. Creating/editing goes through `ContractFormDialog`.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AlertTriangle, FileSignature, Plus } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { ErrorState } from '../components/ui/ErrorState.tsx';
import { SkeletonList } from '../components/ui/Skeleton.tsx';
import { formatDate, money } from '../lib/format.ts';
import { useContracts, useContractSuggestions } from '../lib/hooks/useContracts.ts';
import { CONTRACT_CADENCE } from '../lib/labels.ts';
import type { ContractCadence, ContractOut, ContractSuggestion } from '../lib/types.ts';
import { ContractAlertBadge } from './vertraege/ContractAlertBadge.tsx';
import { ContractFormDialog, type ContractSuggestionPrefill } from './vertraege/ContractFormDialog.tsx';

/** Client-side cadence → monthly-equivalent factor (§6.2 sums footer). */
const MONTHLY_FACTOR: Record<ContractCadence, number> = {
  weekly: 4.33,
  monthly: 1,
  quarterly: 1 / 3,
  half_yearly: 1 / 6,
  yearly: 1 / 12,
};

/** The Auffälligkeiten strip's per-contract message (§6.2 bullet a). */
function alertMessage(contract: ContractOut): string {
  if (contract.alert === 'overdue') {
    // overdue_since is the missed (past) due; next_due is the next FUTURE occurrence
    // and must never be shown as a "seit" date (§3.3 / §6.2 bullet a).
    return contract.overdue_since
      ? `überfällig seit ${formatDate(contract.overdue_since)}`
      : 'überfällig';
  }
  if (contract.alert === 'amount_drift') {
    return contract.last_matched_amount != null
      ? `Betrag weicht ab (${money(contract.expected_amount)} → ${money(contract.last_matched_amount)})`
      : 'Betrag weicht ab';
  }
  return '';
}

function suggestionPrefill(s: ContractSuggestion): ContractSuggestionPrefill {
  return {
    name: s.display_name,
    match_payee: s.match_payee,
    match_sub_merchant: s.match_sub_merchant,
    expected_amount: s.median_amount,
    anchor_date: s.last_booking_date,
    cadence: s.cadence,
    bucket_id: s.bucket_id,
  };
}

export default function VertraegePage() {
  const contractsQuery = useContracts();
  const suggestionsQuery = useContractSuggestions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractOut | null>(null);
  const [suggestionInitial, setSuggestionInitial] = useState<ContractSuggestionPrefill | null>(null);

  const contracts = useMemo(() => contractsQuery.data ?? [], [contractsQuery.data]);
  const alerted = useMemo(() => contracts.filter((c) => c.alert !== null), [contracts]);
  const active = useMemo(() => contracts.filter((c) => c.active), [contracts]);
  const inactive = useMemo(() => contracts.filter((c) => !c.active), [contracts]);

  const monthlyTotal = useMemo(
    () =>
      active
        .filter((c) => Number(c.expected_amount) < 0)
        .reduce((sum, c) => sum + Number(c.expected_amount) * MONTHLY_FACTOR[c.cadence], 0),
    [active],
  );

  function openCreate() {
    setEditingContract(null);
    setSuggestionInitial(null);
    setDialogOpen(true);
  }

  function openEdit(contract: ContractOut) {
    setEditingContract(contract);
    setSuggestionInitial(null);
    setDialogOpen(true);
  }

  function openFromSuggestion(suggestion: ContractSuggestion) {
    setEditingContract(null);
    setSuggestionInitial(suggestionPrefill(suggestion));
    setDialogOpen(true);
  }

  const isPending = contractsQuery.isPending;
  const isError = contractsQuery.isError;

  return (
    <div>
      <PageHeader
        title="Verträge"
        subtitle={contracts.length > 0 ? `${contracts.length} Verträge` : undefined}
        actions={
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Neuer Vertrag</span>
          </Button>
        }
      />

      <div className="w-full space-y-8 px-4 py-6 sm:px-6">
        {isPending ? (
          <SkeletonList rows={4} />
        ) : isError ? (
          <ErrorState error={contractsQuery.error} onRetry={() => void contractsQuery.refetch()} />
        ) : contracts.length === 0 ? (
          <EmptyState
            icon={FileSignature}
            title="Noch keine Verträge"
            description="Lege wiederkehrende Zahlungen (Miete, Netflix, Gehalt) an, um Fälligkeit und Betrag zu überwachen."
            action={
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Ersten Vertrag anlegen
              </Button>
            }
          />
        ) : (
          <>
            {alerted.length > 0 ? (
              <section className="space-y-2">
                <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                  Auffälligkeiten
                </h2>
                <ul className="space-y-1.5">
                  {alerted.map((contract) => (
                    <li
                      key={contract.id}
                      className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                    >
                      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="font-medium">{contract.name}</span>
                      <span className="text-amber-700 dark:text-amber-400">
                        — {alertMessage(contract)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                Aktive Verträge
              </h2>
              {active.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">Keine aktiven Verträge.</p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                  {active.map((contract) => (
                    <ContractListRow key={contract.id} contract={contract} onClick={() => openEdit(contract)} />
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                Vorschläge
              </h2>
              {suggestionsQuery.isPending ? (
                <SkeletonList rows={2} />
              ) : (suggestionsQuery.data ?? []).length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">
                  Keine erkannten wiederkehrenden Zahlungen.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
                  {(suggestionsQuery.data ?? []).map((suggestion) => (
                    <li
                      key={`${suggestion.match_payee}:${suggestion.match_sub_merchant ?? ''}`}
                      className="flex items-center justify-between gap-3 py-3"
                    >
                      <div className="min-w-0 space-y-0.5">
                        <p className="truncate font-medium text-slate-900 dark:text-slate-100">
                          {suggestion.display_name}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {CONTRACT_CADENCE[suggestion.cadence]} · {money(suggestion.median_amount)} ·{' '}
                          {suggestion.occurrences}× erkannt
                        </p>
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => openFromSuggestion(suggestion)}>
                        Als Vertrag anlegen
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {inactive.length > 0 ? (
              <details className="text-sm">
                <summary className="cursor-pointer select-none font-medium text-sky-600 transition-colors duration-150 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">
                  Inaktive Verträge ({inactive.length})
                </summary>
                <ul className="mt-2 divide-y divide-slate-100 dark:divide-slate-800/70">
                  {inactive.map((contract) => (
                    <ContractListRow key={contract.id} contract={contract} onClick={() => openEdit(contract)} />
                  ))}
                </ul>
              </details>
            ) : null}

            <p className="border-t border-slate-100 pt-4 text-sm font-medium text-slate-700 dark:border-slate-800/70 dark:text-slate-300">
              Fixkosten gesamt ≈ {money(Math.abs(monthlyTotal))}/Monat
            </p>
          </>
        )}
      </div>

      <ContractFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contract={editingContract}
        initial={suggestionInitial}
      />
    </div>
  );
}

function ContractListRow({ contract, onClick }: { contract: ContractOut; onClick: () => void }) {
  return (
    <li className="py-3">
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className="flex cursor-pointer items-start justify-between gap-3 rounded-md px-1 py-1 transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800/40"
      >
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-slate-900 dark:text-slate-100">{contract.name}</span>
            {contract.alert ? <ContractAlertBadge alert={contract.alert} /> : null}
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {CONTRACT_CADENCE[contract.cadence]} · {money(contract.expected_amount)}
            {contract.next_due ? ` · fällig ${formatDate(contract.next_due)}` : ''}
          </p>
        </div>
        <Link
          to={`/transaktionen?contract_id=${contract.id}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-sm font-medium text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300"
        >
          Verlauf
        </Link>
      </div>
    </li>
  );
}
